import React, { useState } from 'react'

import { Input, InputField } from '../input'
import { toNumber, toText } from './parse'

export interface NumberInputProps {
    value: number | undefined
    onChange: (value: number | undefined) => void
    onBlur?: () => void
    isInvalid?: boolean
    isDisabled?: boolean
    placeholder?: string
    className?: string
    /** Upper bound. A larger typed value is clamped to it before `onChange`. */
    max?: number
}

// Numeric field backed by its OWN text state so the user can clear it entirely
// and retype — binding the input's value directly to the model number reverts
// the field to the last valid number on the final backspace (and a form/schema
// that coerces empty back to a number would re-restore it on every keystroke).
// The text state is authoritative while the field is mounted; the model receives
// the parsed number (or `undefined` when empty).
//
// No external-value sync: callers mount the field fresh for each value (dialogs
// keyed per open, field-array rows keyed by stable id), so there is no in-place
// `value` swap to reconcile. Adding a resync here re-introduces the clear/retype
// revert above — don't.
export const NumberInput: React.FC<NumberInputProps> = ({
    value,
    onChange,
    onBlur,
    isInvalid,
    isDisabled,
    placeholder,
    className,
    max,
}) => {
    const [text, setText] = useState(() => toText(value))

    return (
        <Input isInvalid={isInvalid} isDisabled={isDisabled} className={className}>
            <InputField
                keyboardType='numeric'
                value={text}
                placeholder={placeholder}
                onChangeText={next => {
                    const parsed = toNumber(next)
                    const clamped =
                        parsed !== undefined && max !== undefined ? Math.min(parsed, max) : parsed
                    // Show the clamp, so the field never displays a value the
                    // model rejected.
                    setText(clamped !== parsed ? String(clamped) : next)
                    onChange(clamped)
                }}
                onBlur={onBlur}
            />
        </Input>
    )
}
NumberInput.displayName = 'NumberInput'
